import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { parseErrorMessage } from "@/lib/queryClient";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { StripeWipeResultPayload } from "./types";
import { STRIPE_WIPE_CONFIRM_PHRASE, STRIPE_WIPE_RESULT_ROWS } from "./constants";

export function StripeWipeTestArtifactsPanel() {
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [lastResult, setLastResult] = useState<StripeWipeResultPayload["result"] | null>(null);
  const [lastRanAt, setLastRanAt] = useState<Date | null>(null);

  const wipeMutation = useMutation<StripeWipeResultPayload, Error>({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await fetch("/api/admin/stripe/wipe-test-artifacts", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        let message = `Wipe failed (HTTP ${res.status})`;
        try {
          const body = await res.json();
          if (body?.message) message = body.message;
        } catch {}
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: (data) => {
      setLastResult(data.result);
      setLastRanAt(new Date());
      setConfirmOpen(false);
      setConfirmText("");
      toast({
        title: "Stripe test artifacts wiped",
        description: "Per-table affected-row counts are shown below.",
      });
    },
    onError: (err) => {
      toast({
        title: "Wipe failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const phraseMatches = confirmText.trim().toLowerCase() === STRIPE_WIPE_CONFIRM_PHRASE;

  const adminHealthQuery = useQuery<{ ok: boolean; stripe: { mode: string; secretKeyConfigured: boolean } }>({
    queryKey: ["/api/admin/health"],
  });
  const stripeMode = adminHealthQuery.data?.stripe?.mode ?? "unknown";

  return (
    <div className="space-y-6" data-testid="panel-stripe-wipe">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2" data-testid="text-stripe-wipe-title">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          Wipe Stripe Test Artifacts
          <span
            className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
              stripeMode === "live"
                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : stripeMode === "test"
                  ? "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800"
                  : "bg-gray-100 text-gray-600 border-gray-200"
            }`}
            data-testid="badge-stripe-mode"
          >
            Stripe environment: {stripeMode}
          </span>
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Clears every Stripe-issued identifier from the local DB and deletes
          per-event Stripe log rows, so this environment can be re-seeded
          against a fresh Stripe test fixture without orphaned pointers.
          Hard-gated to <code className="font-mono">sk_test_…</code> keys —
          the request will be rejected unless <code className="font-mono">STRIPE_MODE=test</code>
          {" "}is active.
        </p>
      </div>

      <Card className="border-red-300 dark:border-red-800 bg-red-50/40 dark:bg-red-950/20 p-4 space-y-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-900 dark:text-red-200 space-y-2">
            <p className="font-medium" data-testid="text-stripe-wipe-danger-heading">Danger Zone</p>
            <p>
              This action runs in a single DB transaction and is irreversible.
              It nulls the Stripe-id columns on retained rows
              (<code className="font-mono">app_users</code>,
              {" "}<code className="font-mono">payment_accounts</code>,
              {" "}<code className="font-mono">vendor_payout_accounts</code>,
              {" "}<code className="font-mono">vendor_credit_cards</code>,
              {" "}<code className="font-mono">payment_transactions</code>),
              resets subscription / billing flags, and deletes from
              {" "}<code className="font-mono">vendor_payouts</code>,
              {" "}<code className="font-mono">billing_proration_events</code>,
              {" "}<code className="font-mono">billing_settlement_errors</code>,
              {" "}<code className="font-mono">referral_host_accruals</code>,
              {" "}<code className="font-mono">referral_host_credits</code>, and
              {" "}<code className="font-mono">processed_stripe_events</code>.
            </p>
          </div>
        </div>
        <div>
          <Button
            variant="destructive"
            onClick={() => {
              setConfirmText("");
              setConfirmOpen(true);
            }}
            disabled={wipeMutation.isPending}
            data-testid="button-open-stripe-wipe-confirm"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Wipe Stripe Test Artifacts
          </Button>
        </div>
      </Card>

      {lastResult && (
        <Card className="p-4 space-y-3" data-testid="card-stripe-wipe-result">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm" data-testid="text-stripe-wipe-result-title">
              Last wipe summary
            </h4>
            {lastRanAt && (
              <span className="text-xs text-muted-foreground" data-testid="text-stripe-wipe-ran-at">
                {lastRanAt.toLocaleString()}
              </span>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Table</TableHead>
                <TableHead className="text-right">Rows affected</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {STRIPE_WIPE_RESULT_ROWS.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="font-mono text-xs">{row.label}</TableCell>
                  <TableCell
                    className="text-right tabular-nums"
                    data-testid={`text-stripe-wipe-count-${row.key}`}
                  >
                    {lastResult[row.key]}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!wipeMutation.isPending) {
            setConfirmOpen(open);
            if (!open) setConfirmText("");
          }
        }}
      >
        <DialogContent
          data-testid="dialog-stripe-wipe-confirm"
          onEscapeKeyDown={(e) => { if (wipeMutation.isPending) e.preventDefault(); }}
          onInteractOutside={(e) => { if (wipeMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (wipeMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Confirm Stripe test data wipe
            </DialogTitle>
            <DialogDescription>
              This action is irreversible. To confirm, type the phrase
              {" "}<span className="font-mono font-semibold">{STRIPE_WIPE_CONFIRM_PHRASE}</span>
              {" "}below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="stripe-wipe-confirm-input">Confirmation phrase</Label>
            <Input
              id="stripe-wipe-confirm-input"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={STRIPE_WIPE_CONFIRM_PHRASE}
              disabled={wipeMutation.isPending}
              data-testid="input-stripe-wipe-confirm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmOpen(false);
                setConfirmText("");
              }}
              disabled={wipeMutation.isPending}
              data-testid="button-stripe-wipe-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => wipeMutation.mutate()}
              disabled={!phraseMatches || wipeMutation.isPending}
              data-testid="button-stripe-wipe-confirm"
            >
              {wipeMutation.isPending ? "Wiping…" : "Wipe test data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
